import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiTrash2, FiEdit2, FiCheckCircle, FiActivity } from 'react-icons/fi';
import { Input, Button, Modal, message, Card } from 'antd';
import PhoneInputPkg from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { apiFetch } from '../api';

const PhoneInput = PhoneInputPkg.default || PhoneInputPkg;

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState('active');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminPhone, setAdminPhone] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/companies');
      const data = await res.json();
      if (res.ok && data.success) {
        setCompanies(data.data || []);
      } else {
        message.error(data.message || 'Failed to fetch companies');
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      message.error('Connection error while fetching companies');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleAddCompany = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim() || !slug.trim() || !adminUsername.trim() || !adminEmail.trim() || !adminPassword.trim() || !adminPhone.trim()) {
      message.error('Please fill in all company and admin fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await apiFetch('/api/companies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          slug: slug.trim(),
          adminUsername: adminUsername.trim(),
          adminEmail: adminEmail.trim(),
          adminPassword: adminPassword.trim(),
          adminPhone: adminPhone.trim(),
          adminName: adminName.trim() || undefined
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        message.success('Company created and admin user seeded successfully!');
        setIsAddModalOpen(false);
        setName('');
        setSlug('');
        setAdminUsername('');
        setAdminEmail('');
        setAdminPassword('');
        setAdminName('');
        setAdminPhone('');
        fetchCompanies();
      } else {
        message.error(data.message || 'Failed to create company');
      }
    } catch (error) {
      console.error('Error adding company:', error);
      message.error('Connection error while creating company');
    }
    setIsSubmitting(false);
  };

  const handleEditCompany = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) {
      message.error('Please enter name');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await apiFetch(`/api/companies/${editingCompany._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), status })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        message.success('Company updated successfully!');
        setIsEditModalOpen(false);
        setEditingCompany(null);
        setName('');
        setStatus('active');
        fetchCompanies();
      } else {
        message.error(data.message || 'Failed to update company');
      }
    } catch (error) {
      console.error('Error editing company:', error);
      message.error('Connection error while updating company');
    }
    setIsSubmitting(false);
  };

  const handleDeleteCompany = (id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this company?',
      content: 'This will delete the company and all associated configs (site config, fields, departments, etc.). This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const res = await apiFetch(`/api/companies/${id}`, {
            method: 'DELETE'
          });
          if (res.ok) {
            message.success('Company deleted successfully');
            fetchCompanies();
          } else {
            const data = await res.json();
            message.error(data.message || 'Failed to delete company');
          }
        } catch (error) {
          console.error('Error deleting company:', error);
          message.error('Connection error while deleting company');
        }
      }
    });
  };

  const openEditModal = (company) => {
    setEditingCompany(company);
    setName(company.name);
    setStatus(company.status);
    setIsEditModalOpen(true);
  };

  return (
    <div className="wm-page p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <FiActivity className="text-blue-500" /> Company Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage corporate entities, custom layouts, and configurations.
          </p>
        </div>
        <Button
          id="btn-add-company"
          type="primary"
          icon={<FiPlus />}
          size="large"
          className="wm-btn-primary flex items-center h-12"
          onClick={() => {
            setName('');
            setSlug('');
            setIsAddModalOpen(true);
          }}
        >
          Add Company
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-[40vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      ) : companies.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[40vh] border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl p-8 bg-white dark:bg-vms-navy">
          <p className="text-slate-500 text-lg font-medium">No companies registered yet.</p>
          <p className="text-slate-400 text-sm mt-1">Get started by clicking the "Add Company" button.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {companies.map((company) => (
              <motion.div
                key={company._id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="bg-white dark:bg-vms-navy border border-slate-100 dark:border-white/10 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      company.status === 'active' ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400' : 'bg-slate-50 dark:bg-slate-900 text-slate-500'
                    }`}>
                      {company.status.toUpperCase()}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(company)}
                        className="text-slate-400 hover:text-blue-500 transition-colors p-1"
                        title="Edit Company"
                      >
                        <FiEdit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteCompany(company._id)}
                        className="text-slate-400 hover:text-red-500 transition-colors p-1"
                        title="Delete Company"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 dark:text-white mb-2">
                    {company.name}
                  </h3>
                  <div className="text-slate-400 dark:text-slate-500 text-sm font-medium">
                    Slug: <code className="bg-slate-50 dark:bg-slate-900 px-2 py-0.5 rounded text-rose-500">{company.slug}</code>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-50 dark:border-white/5 flex items-center justify-between text-xs text-slate-400">
                  <span>Registered: {new Date(company.createdAt).toLocaleDateString()}</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Add Company Modal */}
      <Modal
        title="Register New Company"
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        footer={null}
        destroyOnHidden
        className="dark:bg-slate-900"
      >
        <form onSubmit={handleAddCompany} className="space-y-5 pt-4">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-500 uppercase">Company Name <span className="text-rose-500">*</span></label>
            <Input
              id="input-company-name"
              placeholder="e.g. Elsner Technologies Pvt Lmt"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                // Auto generate slug
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
              }}
              size="large"
              required
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-500 uppercase">Company Slug <span className="text-rose-500">*</span></label>
            <Input
              id="input-company-slug"
              placeholder="e.g. elsner"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
              size="large"
              required
            />
            <span className="text-[10px] text-slate-400">
              The slug is used as an identifier in database contexts.
            </span>
          </div>

          {/* Admin User Information Section */}
          <div className="border-t border-slate-100 dark:border-white/5 pt-4 mt-2">
            <h4 className="text-sm font-bold text-slate-800 dark:text-white mb-3 flex items-center gap-1.5">
              👮 Company Admin Credentials
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase">Admin Full Name (Optional)</label>
                <Input
                  id="input-admin-name"
                  placeholder="e.g. John Doe"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  size="large"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase">Admin Username <span className="text-rose-500">*</span></label>
                <Input
                  id="input-admin-username"
                  placeholder="e.g. john_admin"
                  value={adminUsername}
                  onChange={(e) => setAdminUsername(e.target.value.toLowerCase().replace(/\s+/g, ''))}
                  size="large"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase">Admin Email <span className="text-rose-500">*</span></label>
                <Input
                  id="input-admin-email"
                  type="email"
                  placeholder="e.g. admin@company.com"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  size="large"
                  required
                />
              </div>
              <div className="space-y-1.5 col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase">Admin Phone Number <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <PhoneInput
                    country={'in'}
                    value={adminPhone}
                    onChange={(val) => setAdminPhone(val)}
                    containerClass="!w-full"
                    inputClass="!w-full !h-10 !rounded-lg !border-slate-200 dark:!border-slate-700 dark:!bg-slate-900 dark:!text-white !pl-[48px]"
                    buttonClass="!border-slate-200 dark:!border-slate-700 !rounded-l-lg dark:!bg-slate-800"
                    dropdownClass="dark:!bg-slate-800 dark:!text-white"
                  />
                </div>
              </div>
              <div className="space-y-1.5 col-span-2">
                <label className="text-xs font-bold text-slate-500 uppercase">Admin Password <span className="text-rose-500">*</span></label>
                <Input.Password
                  id="input-admin-password"
                  placeholder="Minimum 6 characters"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  size="large"
                  required
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
            <Button
              id="btn-submit-company"
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              className="wm-btn-primary"
            >
              Create Company
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Company Modal */}
      <Modal
        title="Edit Company Details"
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <form onSubmit={handleEditCompany} className="space-y-5 pt-4">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-500 uppercase">Company Name <span className="text-rose-500">*</span></label>
            <Input
              id="edit-company-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="large"
              required
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-500 uppercase">Status <span className="text-rose-500">*</span></label>
            <select
              id="edit-company-status"
              className="w-full h-10 px-3 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg dark:text-white focus:outline-none"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button
              id="btn-update-company"
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              className="wm-btn-primary"
            >
              Save Changes
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Companies;
